import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { User, Mail, MapPin, Calendar, Edit, Settings, BookOpen, MessageSquare, History, AlertTriangle, Bookmark, RefreshCcw } from 'lucide-react';
import ProfilePictureUpload from '@/components/profile/ProfilePictureUpload';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useReadingHistory } from '@/hooks/useReadingHistory';
import ReadingHistoryList from '@/components/profile/ReadingHistoryList';
import SavedArticlesList from '@/components/profile/SavedArticlesList';
import { useSavedArticles } from '@/hooks/useSavedArticles';

const Profile = () => {
  const { userProfile, user } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { readingHistory, isLoading: historyLoading, error: historyError, refreshHistory } = useReadingHistory();
  const { savedArticles, isLoading: savedArticlesLoading, error: savedArticlesError, refreshSavedArticles, unsaveArticle } = useSavedArticles();
  
  const handleAvatarUpdate = async (url: string | null) => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: url })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast({
        title: 'Profile Picture Updated',
        description: url ? 'Your profile picture has been updated.' : 'Profile picture removed.'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        variant: 'destructive',
        title: 'Update failed',
        description: 'There was a problem updating your profile picture.',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const userData = {
    id: user?.id || 'usr123',
    name: userProfile?.display_name || userProfile?.first_name + ' ' + userProfile?.last_name || 'Anonymous User',
    email: user?.email || 'user@example.com',
    bio: userProfile?.bio || 'No bio available.',
    location: 'San Francisco, CA',
    joinDate: new Date(userProfile?.created_at || Date.now()).toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    }),
    avatar: userProfile?.avatar_url || 'https://i.pravatar.cc/300?img=12',
    interests: ['Technology', 'Science', 'Politics', 'Environment', 'Culture'],
    stats: {
      articlesRead: 127,
      journalistsFollowed: 14,
      commentsPosted: 32
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Your Profile</h1>
          <p className="text-muted-foreground">Manage your personal information and preferences</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-8">
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6 flex flex-col items-center text-center">
                <div className="mb-4">
                  <ProfilePictureUpload 
                    avatarUrl={userData.avatar}
                    onUploadComplete={handleAvatarUpdate}
                    size="lg"
                  />
                </div>
                <h2 className="text-xl font-bold">{userData.name}</h2>
                <p className="text-sm text-muted-foreground mb-4">{userData.email}</p>
                
                <div className="w-full flex items-center justify-center gap-2 mb-4">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Button>
                </div>
                
                <div className="w-full space-y-3 text-sm">
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>{userData.location}</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span>Joined {userData.joinDate}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Activity Stats</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm">Articles Read</span>
                    </div>
                    <span className="font-medium">{userData.stats.articlesRead}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm">Following</span>
                    </div>
                    <span className="font-medium">{userData.stats.journalistsFollowed}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <MessageSquare className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span className="text-sm">Comments</span>
                    </div>
                    <span className="font-medium">{userData.stats.commentsPosted}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Tabs defaultValue="about">
              <TabsList>
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="reading-history">Reading History</TabsTrigger>
                <TabsTrigger value="saved-articles">Saved Articles</TabsTrigger>
                <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
              </TabsList>
              
              <TabsContent value="about" className="mt-6 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">About Me</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>{userData.bio}</p>
                    
                    <Separator className="my-6" />
                    
                    <h3 className="font-medium mb-3">Interests</h3>
                    <div className="flex flex-wrap gap-2">
                      {userData.interests.map(interest => (
                        <Badge key={interest} variant="secondary">{interest}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-3 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Email</p>
                        <p className="text-sm text-muted-foreground">{userData.email}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="reading-history" className="mt-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <History className="h-5 w-5" />
                        Reading History
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Track your recently read articles here.
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={refreshHistory}
                      disabled={historyLoading}
                    >
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <ReadingHistoryList 
                      articles={readingHistory} 
                      isLoading={historyLoading}
                      error={historyError}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="saved-articles" className="mt-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Bookmark className="h-5 w-5" />
                        Saved Articles
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Articles you've saved for later reading.
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={refreshSavedArticles}
                      disabled={savedArticlesLoading}
                    >
                      <RefreshCcw className="mr-2 h-4 w-4" />
                      Refresh
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <SavedArticlesList 
                      articles={savedArticles} 
                      isLoading={savedArticlesLoading}
                      error={savedArticlesError}
                      onUnsave={unsaveArticle}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="subscriptions" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">Your Subscriptions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">Manage your subscriptions to journalists and publications.</p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
